import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/umaang/InstallPrompt";
import SuccessStoriesCarousel from "@/components/umaang/SuccessStoriesCarousel";
import { Heart, Shield, Sparkles, Users, ArrowRight, Star, Instagram, Facebook } from "lucide-react";

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <div className="relative overflow-hidden">
      {/* Decorative color blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 w-96 h-96 rounded-full bg-gradient-to-br from-[#FCA5A5] via-[#F59E0B]/50 to-transparent blur-3xl opacity-70" />
      <div aria-hidden className="pointer-events-none absolute top-40 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#DDD6FE] via-[#F9A8D4]/60 to-transparent blur-3xl opacity-70" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-24 grid md:grid-cols-2 gap-10 items-center relative">
        <div className="fade-in relative">
          <img src="/logo-full.png" alt="Umaang" className="h-24 md:h-28 w-auto mb-2 -ml-1" draggable="false" />
          <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold mb-4">
            · A Rishtā Reimagined ·
          </div>
          <h1 className="font-display font-medium text-5xl md:text-6xl leading-[1.05] tracking-tight">
            {t("hero_h1")}
          </h1>
          <p className="mt-6 text-lg text-[#57534E] leading-relaxed max-w-xl">
            {t("hero_sub")}
          </p>
          <div className="mt-10 flex gap-3 flex-wrap items-center">
            <Link to={user ? "/home" : "/auth?mode=register"} data-testid="hero-cta-start">
              <Button className="btn-pill bg-gradient-to-r from-[#C85A53] via-[#B94238] to-[#A94842] hover:opacity-90 text-white h-12 px-7 shadow-lg shadow-[#C85A53]/30">
                {t("get_started")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/auth" data-testid="hero-cta-signin">
              <Button variant="outline" className="btn-pill h-12 border-[#D6D2C9] bg-white/70 backdrop-blur">
                {t("already_member")}
              </Button>
            </Link>
            <InstallAppButton testId="hero-install-app" label="Install App" className="h-12 px-5" />
          </div>
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
            {[
              { n: "50k+", l: "Trusted profiles",  c: "#C85A53" },
              { n: "10k+", l: "Success stories",   c: "#B45309" },
              { n: "KYC",  l: "Aadhaar verified",  c: "#059669" },
            ].map((s) => (
              <div key={s.l} className="pl-4 border-l-2" style={{ borderColor: s.c }}>
                <div className="font-display text-2xl" style={{ color: s.c }}>{s.n}</div>
                <div className="text-xs text-[#57534E] uppercase tracking-widest mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative fade-in">
          <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#F59E0B]/25 via-[#DB2777]/20 to-[#7C3AED]/20 blur-2xl" aria-hidden />
          <div className="relative aspect-[4/5] overflow-hidden border border-[#D6D2C9] shadow-2xl" style={{ borderRadius: 16 }}>
            <img
              src="https://images.pexels.com/photos/19609201/pexels-photo-19609201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Umaang couple"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-[#D6D2C9] p-5 shadow-[0_12px_40px_rgba(0,0,0,0.12)] max-w-[220px]" style={{ borderRadius: 12 }}>
            <div className="flex items-center gap-2 text-[#C85A53]">
              <Heart className="w-4 h-4" fill="#C85A53" />
              <div className="text-sm font-semibold">92% Match</div>
            </div>
            <div className="mt-2 text-xs text-[#57534E]">Ananya · 27 · Jaipur · MBA</div>
          </div>
          <div className="absolute -top-6 -right-6 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] border border-[#D6D2C9] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.08)]" style={{ borderRadius: 12 }}>
            <div className="text-xs uppercase tracking-widest text-[#7C2D12]/70">Referred by</div>
            <div className="font-display text-sm mt-1 text-[#7C2D12]">Kiran S. · UMG9F3B</div>
          </div>
        </div>
      </section>

      <SuccessStoriesCarousel />

      {/* Features Bento */}
      <section className="border-t border-[#D6D2C9] relative" style={{ background: "linear-gradient(180deg, #FAF7F2 0%, #EAE6DF 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Why Umaang</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 max-w-2xl">
            Every detail crafted for a lifetime decision.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { icon: Heart,    t: "Side-by-side match",    d: "Compare two profiles instantly with our unique 100-point compatibility score.", c: "#C85A53" },
              { icon: Shield,   t: "Verified & private",    d: "Aadhaar/PAN KYC. Contact info is auto-masked in chats. Views stay private to owner.", c: "#059669" },
              { icon: Sparkles, t: "10-photo galleries",    d: "Show your best self. Free members reveal one; premium unlocks all nine more.", c: "#B45309" },
              { icon: Users,    t: "Referral rewards",      d: "Refer a friend — your name lives on their profile as a trust signal.", c: "#2563EB" },
              { icon: Star,     t: "10-day trial",           d: "Full access for ten days. Message up to ten profiles before deciding on premium.", c: "#DB2777" },
              { icon: Heart,    t: "Bilingual UI",           d: "Seamless हिंदी and English toggle — built for every family, every generation.", c: "#7C3AED" },
            ].map((f) => (
              <div key={f.t} className="p-6 bg-white border border-[#D6D2C9] card-hover relative overflow-hidden" style={{ borderRadius: 12 }}>
                <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15" style={{ background: f.c }} />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${f.c}18`, color: f.c }}>
                    <f.icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-xl mt-4">{f.t}</h3>
                  <p className="mt-2 text-sm text-[#57534E] leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#D6D2C9] py-10 text-center text-xs text-[#57534E]">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span>© {new Date().getFullYear()} Umaang — {t("tagline")}</span>
          <nav className="flex items-center gap-5 flex-wrap justify-center" aria-label="Footer">
            <Link data-testid="footer-privacy-link" to="/privacy" className="hover:underline hover:text-[#C85A53]">Privacy Policy</Link>
            <Link data-testid="footer-terms-link" to="/terms" className="hover:underline hover:text-[#C85A53]">Terms of Service</Link>
            <Link data-testid="footer-install-link" to="/install" className="hover:underline hover:text-[#C85A53]">Install App</Link>
            <a
              data-testid="footer-instagram-link"
              href="https://www.instagram.com/umaang.co"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Umaang on Instagram"
              className="w-8 h-8 rounded-full grid place-items-center border border-[#D6D2C9] hover:border-[#C85A53] hover:text-[#C85A53] transition-colors"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.8} />
            </a>
            <a
              data-testid="footer-facebook-link"
              href="https://www.facebook.com/share/1BEp8UBttj/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Umaang on Facebook"
              className="w-8 h-8 rounded-full grid place-items-center border border-[#D6D2C9] hover:border-[#C85A53] hover:text-[#C85A53] transition-colors"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.8} />
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
